import { inject, Injectable } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  AssetMaintenanceDetailsResponse, ReactiveMaintenanceResponse,
  MaintenanceDetailsResponse, ReactiveMaintenanceSummaryResponse
} from '../models/maintenance-overview-response.model';
import { Response } from '../../../../_shared/models/response.model';
import {
  MOCK_IN_MAINTENANCE_DATA,
  MOCK_OVERDUE_MAINTENANCE_DATA,
  MOCK_REACTIVE_MAINTENANCE_DATA,
  MOCK_UPCOMING_MAINTENANCE_DATA,
} from '../mockdata/maintenance-modals-mock.data';
import { AccountDateFilterRequest } from '../../../../_shared/models/accountDateFilter.model';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  BreakdownAnalysisRequest, CriticalAlertsPaginationRequest, EditMaintenanceRequest,
  KpiStatsRequest, MaintenanceOverviewRequest, RescheduleMaintenanceRequest, UpdateMaintenanceStatusRequest
} from '../models/maintenance-overview-request.model';
import { CriticalAlertResponse } from '../models/maintenance-overview-response.model';
import { Pagination, SummaryRequest } from '../../../../_shared/models';
import { DeleteMaintenanceRequest } from '../models/maintenance-overview-request.model';
import { BreakdownAnalysisResponse } from '../models/maintenance-overview.model';
import { KpiStatsResponse } from '../../../../_shared/models/kpi-response.model';
import { CalendarEventsByDateRequest, TodayCalendarEventsPaginationRequest, CalendarEventsPreviewRequest, EditEventScheduleRequest, DragAndDropEventRequest } from '../models/maintenance-calendar-request.model';
import { CalendarEventResponse, EventScheduleResponse, TodayCalendarEventsResponse } from '../models/maintenance-calendar-response.model';
import { LookupWorkshop } from '../../../_shared/models/lookup.model';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceOverviewService {
  private httpClient = inject(HttpClient);

  async getInMaintenanceKpiStats(request: KpiStatsRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/in-maintenance`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getReactiveKpiStats(request: KpiStatsRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/reactive`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getPreventiveKpiStats(request: KpiStatsRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/preventive`, request).pipe(takeUntil(cancelSubject))
    );
  }
  
  async getOverdueKpiStats(request: KpiStatsRequest, cancelSubject: Subject<void>): Promise<Response<KpiStatsResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/overdue`, request).pipe(takeUntil(cancelSubject))
    );
  }
  
  async editMaintenance(payload: EditMaintenanceRequest): Promise<Response<boolean>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/edit-maintenance`, payload)
    );
  }

  async updateMaintenanceStatus(request: UpdateMaintenanceStatusRequest[]): Promise<Response<boolean>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/update-maintenance-status`, request)
    );
  }

  async rescheduleMaintenance(request: RescheduleMaintenanceRequest): Promise<Response<boolean>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/reschedule`, request)
    );
  }

  async deleteAssetMaintenance(request: DeleteMaintenanceRequest): Promise<Response<boolean>> {
    return await firstValueFrom(
      this.httpClient.delete(`${environment.apiEndpoint}/maintenanceoverview/delete-maintenance`, { body: request })
    );
  }
  
  async getAssetMaintenanceById(maintenanceId: number, cancelSubject: Subject<void>): Promise<Response<AssetMaintenanceDetailsResponse>> {
    return await firstValueFrom(
      this.httpClient.get(`${environment.apiEndpoint}/maintenanceoverview/asset-maintenance/${maintenanceId}`).pipe(takeUntil(cancelSubject))
    );
  }

  async getCriticalAlerts(request: CriticalAlertsPaginationRequest, cancelSubject: Subject<void>): Promise<Response<Pagination<CriticalAlertResponse[]>>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/critical-alerts`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getMaintenanceDetails(request: MaintenanceOverviewRequest, cancelSubject: Subject<void>): Promise<Response<Pagination<MaintenanceDetailsResponse[]>>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/maintenance-details`, request).pipe(takeUntil(cancelSubject))
    )
  }

  async getReactiveMaintenanceSummary(request: SummaryRequest, cancelSubject: Subject<void>): Promise<Response<ReactiveMaintenanceSummaryResponse>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/reactive-maintenance-summary`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getReactiveMaintenance(request: AccountDateFilterRequest, cancelSubject: Subject<void>): Promise<Response<ReactiveMaintenanceResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/reactive-maintenance`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getTodayCalendarEvents(request: TodayCalendarEventsPaginationRequest, cancelSubject: Subject<void>): Promise<Response<Pagination<TodayCalendarEventsResponse[]>>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/calendar-events/today`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getCalendarEventsPreview(request: CalendarEventsPreviewRequest, cancelSubject: Subject<void>): Promise<Response<CalendarEventResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(
        `${environment.apiEndpoint}/maintenanceoverview/calendar-events/preview`,
        request
      ).pipe(takeUntil(cancelSubject))
    );
  }

  async getCalendarEventsByDate(request: CalendarEventsByDateRequest, cancelSubject: Subject<void>): Promise<Response<CalendarEventResponse[]>> {
    return await firstValueFrom(
      this.httpClient.post(
        `${environment.apiEndpoint}/maintenanceoverview/calendar-events/date`,
        request
      ).pipe(takeUntil(cancelSubject))
    );
  }

  async getEventSchedule(workOrderId: number, cancelSubject: Subject<void>): Promise<Response<EventScheduleResponse>> {
    return await firstValueFrom(
      this.httpClient.get(`${environment.apiEndpoint}/maintenanceoverview/event-schedule/${workOrderId}`).pipe(takeUntil(cancelSubject))
    );
  }

  async updateEventSchedule(request: EditEventScheduleRequest, cancelSubject: Subject<void>): Promise<Response<boolean>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/event-schedule`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async rescheduleEvent(request: DragAndDropEventRequest, cancelSubject: Subject<void>): Promise<Response<boolean>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/maintenanceoverview/event-schedule/reschedule`, request).pipe(takeUntil(cancelSubject))
    );
  }

  async getServiceCentres(cancelSubject: Subject<void>): Promise<Response<LookupWorkshop[]>> {
    return await firstValueFrom(
      this.httpClient.post(`${environment.apiEndpoint}/asset/lookup/workshops`, { accountIds: [] }).pipe(takeUntil(cancelSubject))
    );
  }

  async getInMaintenanceBreakdown(request: BreakdownAnalysisRequest, cancelSubject: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await this.delay(300);

    return {
      data: MOCK_IN_MAINTENANCE_DATA
    };
  }

  async getOverdueMaintenanceBreakdown(request: BreakdownAnalysisRequest, cancelSubject: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await this.delay(2000);

    return {
      data: MOCK_OVERDUE_MAINTENANCE_DATA
    };
  }

  async getReactiveMaintenanceBreakdown(request: BreakdownAnalysisRequest, cancelSubject: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await this.delay(7000);

    return {
      data: MOCK_REACTIVE_MAINTENANCE_DATA
    };
  }

  async getUpcomingMaintenanceBreakdown(request: BreakdownAnalysisRequest, cancelSubject: Subject<void>): Promise<Response<BreakdownAnalysisResponse[]>> {
    await this.delay(800);

    return {
      data: MOCK_UPCOMING_MAINTENANCE_DATA
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}